import { AlertTriangle, FileText, Printer, Truck } from "lucide-react";
import React from "react";

import type {
  OzonChange,
  OzonChangePlan,
  OzonPlacePlan,
} from "@/api/ozonSupply";
import { cn } from "@/lib/utils";
import { int0 } from "../format";
import { ChangeApplyProgress } from "./ChangeApplyProgress";
import { contentLines, editing, modeWarning } from "./changeModel";

/** Раздел «Что изменится»: дельта по площадке, по 1С и по печати — до того, как что-то уедет.
 *
 *  ⚠️ Это единственное место, где показывается НЕОБРАТИМОЕ: какие места будут сняты с площадки
 *  (коды у них выдаются один раз), какие документы 1С переписаны и какие этикетки станут мусором.
 *  Поэтому пустых состояний тут не бывает: «изменений нет» говорится строкой, ошибка превью —
 *  красным блоком, а не одним тостом, который человек мог и не увидеть.
 */
export function ChangePreview({
  change,
  plan,
  error,
  loading,
  palletsToPortal,
}: {
  change: OzonChange;
  plan: OzonChangePlan | undefined;
  /** паллеты — в портал склада (`onec.pallets_target`): шаг плана паллет в прогрессе каскада */
  palletsToPortal: boolean;
  /** превью не посчиталось — текст отказа */
  error: string | null;
  loading: boolean;
}): React.ReactElement {
  // с первого шага каскад идёт по СОХРАНЁННОМУ плану: показывать пересчитанный — врать про то,
  // что происходит прямо сейчас
  if (!editing(change))
    return (
      <ChangeApplyProgress change={change} palletsToPortal={palletsToPortal} />
    );
  if (error)
    return (
      <div className="space-y-1 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
        <div className="flex items-center gap-1.5 font-medium">
          <AlertTriangle className="size-4 shrink-0" /> План каскада не
          посчитался
        </div>
        <div className="text-xs">{error}</div>
        <div className="text-xs">
          Пока план не сходится, применять нечего: портал не знает, что именно
          он сделает с площадкой и документами 1С. Правка при этом жива — её
          можно продолжить или отменить.
        </div>
      </div>
    );
  if (!plan)
    return (
      <p className="rounded-md border bg-card px-3 py-2 text-sm text-muted-foreground">
        {loading ? "Считаем план каскада…" : "План каскада ещё не считали."}
      </p>
    );

  const warn = modeWarning(plan);
  const rows = contentLines(plan);
  const { unchanged, changed, emptied, new: fresh } = plan.places;
  // ⚠️ Нетронутое считается ДВУМЯ числами: атом правки — короб, и место, у которого сняли один
  // код из тридцати одного, «тронуто» ровно на один короб. Одно число «мест» тут врало бы в обе
  // стороны сразу — и про переклейку, и про то, что уцелело
  const keptBoxes = changed.reduce((n, p) => n + p.kept.length, 0);
  const newBoxes = countNewBoxes(plan);
  return (
    <div className="space-y-2 text-xs">
      {warn && (
        <div className="flex items-start gap-2 rounded-md border-2 border-red-400 bg-red-50 px-3 py-2 text-sm font-medium text-red-800 dark:border-red-800 dark:bg-red-950/60 dark:text-red-200">
          <AlertTriangle className="mt-0.5 size-5 shrink-0" />
          <span>{warn}</span>
        </div>
      )}

      {plan.problems.length > 0 && (
        <div className="space-y-0.5 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
          <b>Применять нельзя:</b>
          {plan.problems.map((p) => (
            <div key={p}>⚠ {p}</div>
          ))}
        </div>
      )}
      {plan.notes.length > 0 && (
        <div className="space-y-0.5 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-800 dark:border-amber-900 dark:bg-amber-950/60 dark:text-amber-200">
          {plan.notes.map((n) => (
            <div key={n}>· {n}</div>
          ))}
        </div>
      )}

      {/* ⚠️ «Изменений нет» — ЯВНОЙ строкой: пустой экран читается как «портал не посчитал» */}
      {plan.mode === "none" && (
        <p className="rounded-md border bg-card px-3 py-2 text-sm text-muted-foreground">
          Изменений нет: раскладка совпадает с тем, что стоит на площадке и
          записано в 1С. Применять нечего — правку можно отменить.
        </p>
      )}

      <Block icon={<Truck className="size-4" />} title="Площадка">
        {rows.length === 0 ? (
          <div className="text-muted-foreground">
            Состав заявки не меняется — площадка держит те же количества.
          </div>
        ) : (
          <table className="w-full border-collapse">
            <thead className="text-muted-foreground">
              <tr className="border-b text-left">
                <th className="py-1 font-normal">Артикул</th>
                <th className="py-1 font-normal">SKU</th>
                <th className="py-1 text-right font-normal">Согласовано</th>
                <th className="py-1 text-right font-normal">Станет</th>
                <th className="py-1 pl-3 font-normal">Что это</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.mp_sku} className="border-b last:border-0">
                  <td className="py-1">{r.article}</td>
                  <td className="py-1 text-muted-foreground">{r.mp_sku}</td>
                  <td className="py-1 text-right tabular-nums">
                    {int0(r.agreed)}
                  </td>
                  <td
                    className={cn(
                      "py-1 text-right font-medium tabular-nums",
                      r.target > r.agreed
                        ? "text-amber-600 dark:text-amber-400"
                        : "text-foreground",
                    )}
                  >
                    {int0(r.target)}
                  </td>
                  <td className="py-1 pl-3 text-muted-foreground">
                    {r.kind === "added"
                      ? "новый артикул — площадка о нём ещё не знает"
                      : r.kind === "removed"
                        ? "не повезём вовсе"
                        : "другое количество"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="mt-1.5 space-y-1">
          <PlaceList
            title="Места на снятие с площадки"
            hint="Коды этих мест будут удалены у площадки — напечатанные на них этикетки станут недействительными"
            places={emptied}
            danger
          />
          <ChangedList places={changed} />
          <PlaceList
            title={
              plan.labels.first_print
                ? "Места на отправку (коды выдаст площадка)"
                : "Места на досылку (коды выдаст площадка)"
            }
            hint="Новые грузоместа: коды им выдаются при отправке, и этикетки на них надо печатать"
            places={fresh}
          />
          {/* ⚠️ Кодов не было вовсе — «нетронутыми остаются 0 мест, их коды живут дальше»
              читалось бы как поломка счёта, а не как самый дешёвый случай правки */}
          <div className="text-muted-foreground">
            {plan.labels.first_print
              ? "Кодов площадки у поставки ещё нет: мест на снятие нет, а все места уедут первой отправкой и получат коды."
              : `Нетронутыми остаются: мест ${int0(unchanged.length)}${
                  unchanged.length > 0 ? ` (№${unchanged.join(", ")})` : ""
                }${
                  changed.length > 0
                    ? `; в изменённых местах нетронуто коробов ${int0(keptBoxes)}`
                    : ""
                } — их коды и этикетки живут дальше.`}
          </div>
        </div>
      </Block>

      <Block icon={<FileText className="size-4" />} title="1С">
        {plan.onec === null ? (
          <div className="text-muted-foreground">
            Документов 1С по этой поставке нет — переписывать нечего.
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {plan.onec.docs.map((d) => (
                <span key={`${d.kind}-${d.number}`}>
                  {docKind(d.kind)} <b>{d.number}</b>
                </span>
              ))}
            </div>
            <div className="mt-1">
              Строки: {delta(plan.onec.rows)} · паллеты:{" "}
              {delta(plan.onec.pallets)}
            </div>
            <div className="mt-0.5 text-muted-foreground">
              Документы правятся НА МЕСТЕ: номера не меняются, распроведение и
              проведение делает сам каскад.
            </div>
          </>
        )}
      </Block>

      <Block icon={<Printer className="size-4" />} title="Печать">
        <div>
          {plan.labels.boxes.length === 0
            ? "Новых коробов правка не заводит — этикетки перепечатывать не нужно."
            : plan.labels.first_print
              ? // ⚠️ кодов до правки не было: этикеток по этой поставке ещё не печатали, и
                // «перепечатать» звало бы искать наклейки, которых нет
                `Кодов до правки не было — это ПЕРВАЯ печать: этикетки мест №${plan.labels.boxes.join(", ")} (${plan.labels.boxes.length}) печатаются обычной кнопкой «Этикетки коробов» после применения.`
              : // ⚠️ Печатается не «место», а НОВЫЕ коробы: у изменённого места уцелевшие коробы
                // стоят со своими наклейками, и стопка «на всю паллету» звала бы склад
                // переклеивать тридцать коробов ради одного
                `Перепечатать этикетки НОВЫХ коробов мест №${plan.labels.boxes.join(", ")} (${
                  newBoxes === null
                    ? "сколько — не посчитать: не заполнен квант"
                    : `${int0(newBoxes)} кор`
                }) — наклейки остальных коробов ${plan.labels.pallets ? "" : "и палет "}верны.`}
        </div>
        {/* ⚠️ «Набор палет меняется» — только когда он ПРАВДА меняется: у изменённой паллеты код
            транспортного места и наклейка те же (замер 18.09.2026), и звать печатать их заново
            значило бы выкинуть верную стопку */}
        <div>
          {plan.labels.pallets
            ? "Набор палет меняется — наклейки палет печатаются заново целиком."
            : "Набор палет не меняется — наклейки палет прежние."}
        </div>
        <div className="text-muted-foreground">
          Сборочный лист после применения перепечатывается целиком: по нему
          собирают поставку, а не дельту.
        </div>
      </Block>
    </div>
  );
}

/** Один блок превью. Три системы — три блока, и каждая отвечает на свой вопрос. */
function Block({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="rounded-md border bg-card px-3 py-2">
      <div className="mb-1 flex items-center gap-1.5 font-medium">
        {icon}
        {title}
      </div>
      {children}
    </div>
  );
}

function PlaceList({
  title,
  hint,
  places,
  danger = false,
}: {
  title: string;
  hint: string;
  places: OzonPlacePlan[];
  danger?: boolean;
}): React.ReactElement | null {
  if (places.length === 0) return null;
  return (
    <div
      className={cn(
        "rounded border px-2 py-1",
        danger
          ? "border-red-300 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
          : "border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300",
      )}
      title={hint}
    >
      <b>
        {title}: {places.length}
      </b>
      <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
        {places.map((p) => (
          <span key={p.position} className="whitespace-nowrap">
            №{p.position} {kind(p.kind)}
            {p.boxes !== null && ` · ${int0(p.boxes)} кор`}
            {/* хвосты кодов — по ним сверяют место глазами, как на этикетке площадки */}
            {p.cargo_ids.length > 0 &&
              ` · коды …${p.cargo_ids.map((c) => c.slice(-6)).join(", …")}`}
            {p.transport_cargo_id &&
              ` · палета …${p.transport_cargo_id.slice(-6)}`}
          </span>
        ))}
      </div>
    </div>
  );
}

/** Места, у которых правка тронула ЧАСТЬ коробов: само место живёт дальше со своим номером,
 *  паллетой и наклейкой — меняются только коробы.
 *
 *  ⚠️ Отдельным блоком от «на снятие»: там место уходит с площадки целиком, здесь — остаётся.
 *  Сваленные в один список, они звали бы искать место, которое никуда не девается, и наоборот.
 *  Три числа на строку («снять / нетронуты / дослать») — это и есть ответ на «что переклеивать».
 */
function ChangedList({
  places,
}: {
  places: OzonPlacePlan[];
}): React.ReactElement | null {
  if (places.length === 0) return null;
  return (
    <div
      className="rounded border border-amber-300 bg-amber-50 px-2 py-1 text-amber-800 dark:border-amber-900 dark:bg-amber-950/60 dark:text-amber-200"
      title="Коды снимаются поштучно: уцелевшие коробы остаются у площадки со своими наклейками, а недостающее уедет досылкой в это же место"
    >
      <b>Места, где меняются коробы: {places.length}</b>
      <div className="mt-0.5 space-y-0.5">
        {places.map((p) => (
          <div key={p.position} className="whitespace-nowrap">
            №{p.position} {kind(p.kind)} · снять коды:{" "}
            {int0(p.cargo_ids.length)}
            {/* хвосты кодов — по ним сверяют короб глазами, как на этикетке площадки */}
            {p.cargo_ids.length > 0 &&
              ` (…${p.cargo_ids.map((c) => c.slice(-6)).join(", …")})`}
            {" · нетронуты: "}
            {int0(p.kept.length)}
            {" · дослать: "}
            {/* ⚠️ Не посчитанный квант — это «не знаем», а не «ноль коробов»: ноль означал бы,
                что досылать нечего, и правка выглядела бы законченной */}
            {p.boxes_new === null
              ? "не посчитать: не заполнен квант"
              : `${int0(p.boxes_new)} кор`}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Сколько НОВЫХ коробов заведёт правка — столько этикеток и печатать. `null` — не посчитать
 *  (у кого-то не заполнен квант), и это честнее любого числа: стопка вышла бы неполной. */
function countNewBoxes(plan: OzonChangePlan): number | null {
  const counts = [
    ...plan.places.changed.map((p) => p.boxes_new),
    ...plan.places.new.map((p) => p.boxes),
  ];
  return counts.some((n) => n === null)
    ? null
    : counts.reduce((sum: number, n) => sum + (n ?? 0), 0);
}

const kind = (k: string): string => (k === "PALLET" ? "паллета" : "короб");

/** Документ 1С — тем словом, которым его зовут в учёте, а не служебным ключом плана.
 *  Незнакомый вид показываем как есть: выдумывать ему имя хуже, чем показать ключ. */
const DOC_KIND: Record<string, string> = {
  order: "заказ",
  transfer: "ПТМО",
  invoice: "счёт-фактура",
  pallets: "паллеты",
  pallet_plan: "план паллет в портале склада",
};

const docKind = (k: string): string => DOC_KIND[k] ?? k;

/** Дельта строк одной фразой. Пустая — «без изменений», а не три нуля. */
function delta(d: Record<string, number>): string {
  const parts = [
    d.added ? `+${d.added}` : "",
    d.changed ? `изменено ${d.changed}` : "",
    d.removed ? `−${d.removed}` : "",
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : "без изменений";
}
